import React, { useState, useEffect, useRef } from "react";
import { Autocomplete, TextField, Dialog, DialogTitle, DialogContent, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip, ButtonGroup, Box,
} from "@mui/material";
import { ContentCopy as ContentCopyIcon, Delete as DeleteIcon, Preview as PreviewIcon
} from "@mui/icons-material";
import Text from "../../utils/commonComponent/Custom/text";
import { AddButton } from "../../utils/commonComponent/Custom/Button";
import { fetchDisciplineGroupDetails, fetchTestMethodByMaterialId, fetchUnitListdisGrpWise, fetchTestMethodAll
} from "./fetchHandler";
import LoaderModal from "../../utils/loader.js/loader";
import { InwardhideColumn } from "../../global";

// *** DEFAULT UNITS - Remove this section when API is ready ***
  const DEFAULT_UNITS = [
    { id: 1, symbol: "m" }, 
    { id: 2, symbol: "cm" }, 
    { id: 3, symbol: "mm" }
  ];

  // Add certificateType options at the top with other constants
  // const CERTIFICATE_TYPE_OPTIONS = [
  //   { id: 1, name: "Separate" },
  //   { id: 2, name: "Combine" }
  // ];
const OBSERVATION_TYPE_LABEL = { 1: "Separate", 2: "Combine" };

// Parse comma-separated IDs string → array of numbers
const parseIds = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val.map(Number);
  return String(val).split(",").map(Number).filter(Boolean);
};
// Format array of IDs → comma-separated string
const formatIds = (arr) => arr.join(",");

const InwardTestRows = ({
  params,
  materialList,
  unitList,
  testRows,
  setTestRows,
  deletedIds = [], // Array to store deleted row IDs
  setDeletedIds = () => {}, // Function to update deleted IDs
  extraColumns = [], // New prop for extra columns like ["type", "sizeball"]
  permissions,
  disgroupList = [],
  inputStates,
}) => {
  const preloadDoneRef = useRef(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [testOptionsCache, setTestOptionsCache] = useState({});
  const [copyQt, setCopyQt] = useState([]);
  const [openModal, setOpenModal] = useState({ open: false, index: null });  
  const [loading, setLoading] = useState(false); // ← loader ADD THIS

  const [rowMaterialLists, setRowMaterialLists] = useState({});
  const [rowUnitLists, setRowUnitLists] = useState({});

  // Column Configuration
  const fixedColumns = [
    // { key: "certificateType", label: "Certificate Type", type: "autocomplete" },
    { key: "boreHoleNo", label: "BH No./Trial Pit No.", type: "text", hideWhen: ({ location }) => location == 2},
    { key: "locationChainageNo", label: "Location/Chainage No./Source.", type: "text", hideWhen: ({ location }) => location == 2},
    { key: "lotNumber", label: "Lot No.", type: "text", hideWhen: ({ location }) => location == 2},
    { key: "fromDepth", label: "From Depth", type: "valueWithUnit",hideWhen: ({ location }) => location == 2},
    { key: "toDepth", label: "To Depth", type: "valueWithUnit",hideWhen: ({ location }) => location == 2},
    { key: "sampleType", label: "Sample Type", type: "text",hideWhen: ({ location }) => location == 2},
    { key: "sampleCondition", label: "Sample Condition", type: "text",hideWhen: ({ location }) => location == 2},
    { key: "SamplingDetails", label: "Sample Details", type: "text",hideWhen: ({ location }) => location == 2},
    { key: "quantity", label: "Quantity Of Sample", type: "text",hideWhen: ({ location }) => location == 2},
    { key: "lengthOfRock", label: "Length Of Rock", type: "valueWithUnit", hideWhen: ({ location }) => location == 2},
    { key: "groupId", label: "Group *", type: "autocomplete", hideWhen: ({ location }) => location == 2},
    { key: "materialId", label: "Material *", type: "autocomplete", hideWhen: ({ location }) => location == 2},
    { key: "testId", label: "Test *", type: "autocomplete" },
  ];

  const isColumnHidden = (column) => {
  if (InwardhideColumn.includes(column.key)) return true; // column hide from InwardhideColumn
    if (typeof column.hideWhen === "function") {
      return column.hideWhen({ location: inputStates.location });
    }
    return false;
  };

  // Helper functions for value#unitSymbol format (FIXED)
  const parseValueWithUnit = (valueString) => {
    if (!valueString) return { value: "", unitSymbol: null };
    const parts = valueString.split("#");
    return { value: parts[0] || "",
      unitSymbol: parts[1] || null,
    };
  };

  const formatValueWithUnit = (value, unitSymbol) => {
    if (!value && !unitSymbol) return "";
    if (!value) return "";
    return unitSymbol ? `${value}#${unitSymbol}` : value;
  };

  const formatValueWithUnitDisplay = (valueString) => {
    if (!valueString) return "";
    const { value, unitSymbol } = parseValueWithUnit(valueString);
    if (!value) return "";

    return unitSymbol ? `${value} ${unitSymbol}` : value;
  };

  // FIXED: Preload materials, units, and test methods for existing rows during edit mode
  useEffect(() => {
      const existingRows = testRows.filter(row => row.id && !row.isLocal);
      if (!existingRows.length || preloadDoneRef.current) return;

      const fetchRowData = async () => {
        setLoading(true);
        try {
        for (let idx = 0; idx < existingRows.length; idx++) {
          const row = existingRows[idx];
          const groupIds = parseIds(row.groupId);

          if (groupIds.length > 0) {
            const allMaterials = [];
            const allUnits = [...DEFAULT_UNITS];
            const seenMaterialIds = new Set();
            const seenUnitIds = new Set(DEFAULT_UNITS.map((u) => u.id));

            await Promise.all(
              groupIds.map((groupId) => {
                const inputState = { ...inputStates, groupId };
                return new Promise((resolve) => {
                  fetchDisciplineGroupDetails(inputState, (materialList) => {
                    materialList.forEach((m) => {
                      if (!seenMaterialIds.has(m.id)) {
                        seenMaterialIds.add(m.id);
                        allMaterials.push(m);
                      }
                    });
                    fetchUnitListdisGrpWise(inputState, (fetchedUnits) => {
                      fetchedUnits.forEach((u) => {
                        if (!seenUnitIds.has(u.id)) {
                          seenUnitIds.add(u.id);
                          allUnits.push(u);
                        }
                      });
                      resolve();
                    });
                  });
                });
              })
            );

            setRowMaterialLists(prev => ({ ...prev, [idx]: allMaterials }));
            setRowUnitLists(prev => ({ ...prev, [idx]: allUnits }));
          }

          // Fetch test methods for all selected materials
          const materialIds = parseIds(row.materialId);
          for (const materialId of materialIds) {
            if (!testOptionsCache[materialId]) {
              const methods = await fetchTestMethodByMaterialId(materialId);
              setTestOptionsCache(prev => ({ ...prev, [materialId]: methods }));
            }
          }
        }
        } catch (err) {
          console.error(err);
        }
        finally {
           setLoading(false);
        }
      };

      fetchRowData().catch(console.error);
      preloadDoneRef.current = true;
    }, [testRows]);


  // Initialize copy quantity array when testRows change
  useEffect(() => {
    if (copyQt.length !== testRows.length) {
      setCopyQt(Array(testRows.length).fill(1));
    }
  }, [testRows.length]);

  // *** NEW: Initialize default units for all rows ***
  useEffect(() => {
    const initialUnitLists = {};
    testRows.forEach((_, index) => {
      if (!rowUnitLists[index]) {
        initialUnitLists[index] = DEFAULT_UNITS;
      }
    });
    
    if (Object.keys(initialUnitLists).length > 0) {
      setRowUnitLists(prev => ({ ...prev, ...initialUnitLists }));
    }
  }, [testRows.length]);
  // *** END NEW ***

  const notifyParent = (newRows) => setTestRows(newRows);

  // Initialize extra columns in row object
  const initializeExtraColumns = () => {
    const extraColumnsObj = {};
    extraColumns.forEach((col) => {
      extraColumnsObj[col] = "";
    });
    return extraColumnsObj;
  };

  // Add new row at the end
  const addRow = () => {
    const newRow = {
      id: null, // No ID for new rows (local)
      inwardId: params?.id || 0,
      // certificateType: inputStates.location == 1 ? 2 : 1,
      groupId: "",      // empty string for multi-select
      materialId: "",   // empty string for multi-select
      testId: null,
      boreHoleNo: "",
      locationChainageNo: "",
      lotNumber: "",
      fromDepth: "",
      toDepth: "",
      sampleType: "",
      sampleCondition: "",
      SamplingDetails: "",
      quantity: "",
      lengthOfRock: "",
      // extraColumns: initializeExtraColumns(),
      isLocal: true // Flag to identify local rows
    };
    const newIndex = testRows.length;
    notifyParent([...testRows, newRow]);
    setEditingIndex(newIndex);
    setRowUnitLists(prev => ({ ...prev, [newIndex]: DEFAULT_UNITS }));
    setCopyQt([...copyQt, 1]);
  };

  // Update cell value
  const updateCellValue = async (index, key, value) => {
    const newRows = testRows.map((row, i) =>
      i === index ? { ...row, [key]: value } : row
    );

    // Handle groupId change → fetch materials + units for ALL selected groups
    if (key === "groupId") {
      const groupIds = parseIds(value);

      if (groupIds.length === 0) {
        setRowMaterialLists((prev) => ({ ...prev, [index]: [] }));
        setRowUnitLists((prev) => ({ ...prev, [index]: DEFAULT_UNITS }));
      } else {
        setLoading(true);
        try {
          const allMaterials = [];
          const allUnits = [...DEFAULT_UNITS];
          const seenMaterialIds = new Set();
          const seenUnitIds = new Set(DEFAULT_UNITS.map((u) => u.id));

          await Promise.all(
            groupIds.map((groupId) => {
              const updatedInputStates = { ...inputStates, groupId };
              return new Promise((resolve) => {
                fetchDisciplineGroupDetails(updatedInputStates, (materialList) => {
                  materialList.forEach((m) => {
                    if (!seenMaterialIds.has(m.id)) {
                      seenMaterialIds.add(m.id);
                      allMaterials.push(m);
                    }
                  });
                  fetchUnitListdisGrpWise(updatedInputStates, (unitList) => {
                    unitList.forEach((u) => {
                      if (!seenUnitIds.has(u.id)) {
                        seenUnitIds.add(u.id);
                        allUnits.push(u);
                      }
                    });
                    resolve();
                  });
                });
              });
            })
          );

          setRowMaterialLists((prev) => ({ ...prev, [index]: allMaterials }));
          setRowUnitLists((prev) => ({ ...prev, [index]: allUnits }));
        } catch (error) {
          console.error("Error fetching group details:", error);
          setRowUnitLists((prev) => ({ ...prev, [index]: DEFAULT_UNITS }));
        } finally { 
          setLoading(false);
        }
      }

      // reset material/test when group changes
      newRows[index].materialId = "";
      newRows[index].testId = null;
    }

    // Handle materialId change → fetch test methods for ALL selected materials
    if (key === "materialId" && value) {
      newRows[index].testId = null;
      const ids = parseIds(value);
      const uncachedIds = ids.filter((id) => !testOptionsCache[id]); // ← ADD: only fetch what's not cached
      if (uncachedIds.length > 0) { // ← ADD: only show loader if there's something to fetch
        setLoading(true);
        try {
      await Promise.all(
          uncachedIds // ← MODIFIED: was `ids`, now only uncached ones
          .map(async (id) => {
            try {
              const testMethods = await fetchTestMethodByMaterialId(id);
              setTestOptionsCache((prev) => ({ ...prev, [id]: testMethods }));
            } catch (error) {
              console.error("Error fetching test methods for material", id, error);
            }
          })
      );
        } catch (error) {
          console.error(error);
        } finally {
          setLoading(false);
        }
      }
    }

    notifyParent(newRows);
  };

  // Update value with unit (FIXED to use unitSymbol)
  const updateValueWithUnit = (index, key, value, unitSymbol) => {
    const formattedValue = formatValueWithUnit(value, unitSymbol);
    updateCellValue(index, key, formattedValue);
  };

  // Copy row based on quantity
  const copyRow = (index) => {
    const quantity = copyQt[index] || 1;
    const rowToCopy = testRows[index];

    // Create multiple copies with proper extra columns and flags
    const copiedRows = Array(quantity).fill().map(() => ({
      ...rowToCopy,
      id: null, // New copies are local
      isLocal: true,
      // extraColumns: { ...rowToCopy.extraColumns }, // deep copy extra columns
    }));
    
    // Add copies at the end
    const newTestRows = [...testRows, ...copiedRows];
    notifyParent(newTestRows);

    const newRowMaterialLists = { ...rowMaterialLists };
    const newRowUnitLists = { ...rowUnitLists };

    for (let i = 0; i < quantity; i++) {
      const newIndex = testRows.length + i;
      newRowMaterialLists[newIndex] = rowMaterialLists[index] || [];
      // *** MODIFIED: Use default units if no units exist for source row ***
      newRowUnitLists[newIndex] = rowUnitLists[index] || DEFAULT_UNITS;
    }

  setRowMaterialLists(newRowMaterialLists);
  setRowUnitLists(newRowUnitLists);

    setCopyQt(prev => [...prev, ...Array(quantity).fill(1)]);
  };

  // Delete row
  const deleteRow = (index) => {
    if (window.confirm("Are you sure you want to delete this row?")) {
      const rowToDelete = testRows[index];
      
      if (rowToDelete.id && !rowToDelete.isLocal) {
        setDeletedIds([...deletedIds, rowToDelete.id]);
      }
      
      // Remove row from testRows
      const newRows = testRows.filter((_, i) => i !== index);
      notifyParent(newRows);
      
      // Remove corresponding copyQt entry
      const newCopyQt = copyQt.filter((_, i) => i !== index);
      setCopyQt(newCopyQt);
      
      // Reset editing if deleting currently edited row
      if (editingIndex === index) {
        setEditingIndex(null);
      } else if (editingIndex > index) {
        setEditingIndex(editingIndex - 1);
      }
    }
  };

  // Handle row click to make it editable
  const handleRowClick = (index) => {
    setEditingIndex(index === editingIndex ? null : index);
  };

  // Render value with unit field (FIXED)
  const renderValueWithUnitField = (
    row,
    rowIndex,
    key,
    label,
    isEditing,
    isModal = false
  ) => {
    const { value, unitSymbol } = parseValueWithUnit(row[key]);
    const unitListForRow = rowUnitLists[rowIndex] || DEFAULT_UNITS;
    const selectedUnit = unitListForRow.find((u) => u.symbol === unitSymbol);

    const cellStyle = {
      minWidth: "180px",
      minHeight: "58px",
      border: isModal ? "none" : "1px solid #ddd",
      padding: isModal ? "0px" : "10px",
      cursor: !isEditing ? "pointer" : "default",
    };

    if (isEditing) {
      return (
        <TableCell
          style={{ ...cellStyle  }}
          key={`row-${rowIndex}-${key}`}
        >
          <Box display="flex" alignItems="center">
            <TextField
              size="small"
              value={value}
              onChange={(e) =>
                updateValueWithUnit(rowIndex, key, e.target.value, unitSymbol)
              }
              label={isModal ? label : ""}
              style={{ width: "120px" }}
            />
            <Autocomplete
              size="small"
              style={{ width: "100px" }}
              value={selectedUnit || null}
              options={unitListForRow}
              getOptionLabel={(option) => option.symbol || ""}
              onChange={(_, selectedUnit) =>
                updateValueWithUnit(
                  rowIndex,
                  key,
                  value,
                  selectedUnit?.symbol || null
                )
              }
              renderInput={(params) => (
                <TextField {...params} label={isModal ? "Unit" : ""} />
              )}
            />
          </Box>
        </TableCell>
      );
    } else {
      // Just show the value in view mode
      return (
        <TableCell
          style={cellStyle}
          key={`row-${rowIndex}-${key}`}
          onClick={() => handleRowClick(rowIndex)}
        >
          {formatValueWithUnitDisplay(row[key]) || ""}
        </TableCell>
      );
    }
  };


  // Render cell based on type and mode
  const renderCell = (row, rowIndex, column, isEditing, isModal = false) => {
    const { key, type, label } = column;
    const cellStyle = {
      minWidth: "180px",
      minHeight: "58px",
      border: isModal ? "none" : "1px solid #ddd",
      padding: isModal ? "0px" : "10px",
      cursor: !isEditing ? "pointer" : "default"
    };

    if (key === "id") {
      return (
        <TableCell 
          style={cellStyle} 
          key={`row-${rowIndex}-${key}`}
          onClick={() => !isEditing && handleRowClick(rowIndex)}
        >
          {rowIndex + 1}
        </TableCell>
      );
    }

    if (type === "valueWithUnit") {
      return renderValueWithUnitField(
        row,
        rowIndex,
        key,
        label,
        isEditing,
        isModal
      );
    }

    if (type === "autocomplete") {
      if (key === "materialId") {
        const materialListForRow = rowMaterialLists[rowIndex] || [];
        // Parse stored "1,2,3" → array of material objects
        const selectedMaterials = materialListForRow.filter((m) =>
          parseIds(row[key]).includes(m.id)
        );

        if (isEditing) {
          return (
            <TableCell style={{ ...cellStyle, cursor: "default" }} key={`row-${rowIndex}-${key}`}>
              <Autocomplete
                multiple
                style={{ width: "220px" }}
                size="small"
                limitTags={1}
                value={selectedMaterials}
                options={materialListForRow}
                getOptionLabel={(option) => `${option.MaterialName || ""}${option.subType ? ` (${option.subType})` : ""}`}
                onChange={(_, values) => {
                  const ids = formatIds(values.map((v) => v.id));
                  updateCellValue(rowIndex, key, ids);
                }}
                renderInput={(params) => (
                  <TextField {...params} label={isModal ? "Material *" : ""} />
                )}
              />
            </TableCell>
          );
        } else {
          return (
            <TableCell 
              style={cellStyle} 
              key={`row-${rowIndex}-${key}`}
              onClick={() => handleRowClick(rowIndex)}
            >
              {selectedMaterials.length === 0
                ? ""
                : selectedMaterials.length === 1
                ? `${selectedMaterials[0].MaterialName}${selectedMaterials[0].subType ? ` (${selectedMaterials[0].subType})` : ""}`
                : `${selectedMaterials[0].MaterialName}${selectedMaterials[0].subType ? ` (${selectedMaterials[0].subType})` : ""} +${selectedMaterials.length - 1}`}
            </TableCell>
          );
        }
      }

      if (key === "testId") {
        // Merge test options from all selected materials
        const testOptions = inputStates.location == 2
          ? testOptionsCache.ALL || []
          : parseIds(row.materialId)
              .flatMap((mid) => testOptionsCache[mid] || [])
              .filter((t, i, arr) => arr.findIndex((x) => x.id === t.id) === i); // dedupe

        const selectedTest = testOptions.filter((t) => {
          const ids = Array.isArray(row[key])
            ? row[key]
            : typeof row[key] === "string"
            ? row[key].split(",").map((id) => parseInt(id, 10))
            : [row[key]];

          return ids.includes(t.id);
        });
        if (isEditing) {
          return (
            <TableCell style={{ ...cellStyle, cursor: "default" }} key={`row-${rowIndex}-${key}`}>
              <Autocomplete
                multiple
                style={{ width: "220px" }}
                size="small"
                limitTags={1}
                value={selectedTest}
                options={testOptions}
                getOptionLabel={(option) => `${option.testName} (${option.testMethod}) — (${OBSERVATION_TYPE_LABEL[option.observationType] || "-"})`
                }
                onChange={(_, values) => {
                  const ids = values.map((v) => v.id);
                  const observationMap = {};
                  values.forEach(v => { observationMap[v.id] = v.observationType; });

                  const newRows = testRows.map((row, i) =>
                    i === rowIndex
                      ? { ...row, testId: ids.join(","), observationTypeMap: observationMap }
                      : row
                  );
                  notifyParent(newRows);
                }}
                renderInput={(params) => (
                  <TextField {...params} label={isModal ? "Test *" : ""} />
                )}
              />
            </TableCell>
          );
        } else {
          return (
            <TableCell 
              style={cellStyle} 
              key={`row-${rowIndex}-${key}`}
              onClick={() => handleRowClick(rowIndex)}
            >
              {selectedTest && selectedTest.length > 0
              ? selectedTest.length === 1
                ? `${selectedTest[0]?.testName || ""} (${OBSERVATION_TYPE_LABEL[selectedTest[0]?.observationType] || "-"})`
                : `${selectedTest[0]?.testName} (${OBSERVATION_TYPE_LABEL[selectedTest[0]?.observationType] || "-"}) +${selectedTest.length - 1}` : ""}
            </TableCell>
          );
        }
      }

      if (key === "groupId") {
        // Parse stored "1,2,3" → array of group objects
        const selectedGroups = disgroupList.filter((g) =>
          parseIds(row[key]).includes(g.id)
        );

        if (isEditing) {
          return (
            <TableCell
              style={{ ...cellStyle, cursor: "default" }}
              key={`row-${rowIndex}-${key}`}
            >
              <Autocomplete
                multiple
                style={{ width: "220px" }}
                size="small"
                limitTags={1}
                value={selectedGroups}
                options={disgroupList}
                getOptionLabel={(option) => option.groupName || ""}
                onChange={(_, values) =>
                  updateCellValue(rowIndex, key, formatIds(values.map((v) => v.id)))
                }
                renderInput={(params) => (
                  <TextField {...params} label={isModal ? "Group *" : ""} />
                )}
              />
            </TableCell>
          );
        } else {
          return (
            <TableCell
              style={cellStyle}
              key={`row-${rowIndex}-${key}`}
              onClick={() => handleRowClick(rowIndex)}
            >
              {selectedGroups.length === 0
                ? ""
                : selectedGroups.length === 1
                ? selectedGroups[0].groupName
                : `${selectedGroups[0].groupName} +${selectedGroups.length - 1}`}
            </TableCell>
          );
        }
      }

      // if (key === "certificateType") {
      //   const selectedType = CERTIFICATE_TYPE_OPTIONS.find((t) => t.id == row[key]);

      //   if (isEditing) {
      //     return (
      //       <TableCell
      //         style={{ ...cellStyle, cursor: "default" }}
      //         key={`row-${rowIndex}-${key}`}
      //       >
      //         <Autocomplete
      //           style={{ width: "220px" }}
      //           size="small"
      //           value={selectedType || null}
      //           options={CERTIFICATE_TYPE_OPTIONS}
      //           getOptionLabel={(option) => option.name || ""}
      //           onChange={(_, value) =>
      //             updateCellValue(rowIndex, key, value?.id || null)
      //           }
      //           renderInput={(params) => (
      //             <TextField {...params} label={isModal ? "Testing Type *" : ""} />
      //           )}
      //         />
      //       </TableCell>
      //     );
      //   } else {
      //     return (
      //       <TableCell
      //         style={cellStyle}
      //         key={`row-${rowIndex}-${key}`}
      //         onClick={() => handleRowClick(rowIndex)}
      //       >
      //         {selectedType?.name || ""}
      //       </TableCell>
      //     );
      //   }
      // }
    }

    if (type === "text") {
      if (isEditing) {
        return (
          <TableCell style={{ ...cellStyle, cursor: "default" }} key={`row-${rowIndex}-${key}`}>
            <TextField
              style={{ width: "220px" }}
              size="small"
              value={row[key] || ""}
              onChange={(e) => updateCellValue(rowIndex, key, e.target.value)}
              label={isModal ? column.label : ""}
              inputProps={{ autoComplete: 'off' }}
            />
          </TableCell>
        );
      } else {
        return (
          <TableCell 
            style={cellStyle} 
            key={`row-${rowIndex}-${key}`}
            onClick={() => handleRowClick(rowIndex)}
          >
            {row[key] || ""}
          </TableCell>
        );
      }
    }

    return (
      <TableCell 
        style={cellStyle} 
        key={`row-${rowIndex}-${key}`}
        onClick={() => !isEditing && handleRowClick(rowIndex)}
      >        
      </TableCell>
    );
  };

  // Render extra columns based on props
  const renderExtraColumns = (row, rowIndex, isEditing, isModal = false) => {
    if (!extraColumns || extraColumns.length === 0) return null;
    
    // Ensure row has extraColumns object initialized
    if (!row.extraColumns) {
      row.extraColumns = initializeExtraColumns();
    }
    
    return extraColumns.map((extraKey, index) => {
      const cellStyle = {
        minWidth: "180px",
        minHeight: "58px",
        border: isModal ? "none" : "1px solid #ddd",
        padding: isModal ? "0px" : "10px",
        cursor: !isEditing ? "pointer" : "default"
      };

      if (isEditing) {
        return (
          <TableCell style={cellStyle} key={`row-${rowIndex}-extra-${index}`}>
            <TextField
              style={{ width: "220px" }}
              size="small"
              value={row.extraColumns[extraKey] || ""}
              onChange={(e) => {
                updateCellValue(rowIndex, "extraColumns", {
                  ...row.extraColumns,
                  [extraKey]: e.target.value
                });
              }}
              label={isModal ? extraKey : ""}
              inputProps={{ autoComplete: 'off' }}
            />
          </TableCell>
        );
      } else {
        return (
          <TableCell 
            style={cellStyle} 
            key={`row-${rowIndex}-extra-${index}`}
            onClick={() => handleRowClick(rowIndex)}
          >
            {row.extraColumns[extraKey] || ""}
          </TableCell>
        );
      }
    });
  };

  useEffect(() => {
    if (inputStates.location == 2 && !testOptionsCache.ALL) {
      setLoading(true); // ← ADD
      fetchTestMethodAll()
        .then((res) => {
          setTestOptionsCache((prev) => ({
            ...prev,
            ALL: res,
          }));
        })
        .catch((error) => console.error(error))
        .finally(() => setLoading(false));
    }
  }, [inputStates.location]);


  // Open modal for editing
  const openModalEdit = (open, index, row) => {
    setOpenModal({ open, index });
  };

  return (
    <div className="mt-6">
      {/* add loader to the page */}
      <LoaderModal loading={loading} />

      <div className="relative flex items-center mb-6">
        {!(testRows.length > 0 && inputStates.location == 2) && (
          <div className="absolute left-0">
            <AddButton id="addtest" onClick={addRow} label="Add Test" />
          </div>
        )}
        <div className="mx-auto">
          <Text variant="h4" bold>
            Test Details
          </Text>
        </div>
      </div>

      {/* Table */}
      <TableContainer component={Paper}  style={{ maxHeight: "650px", overflow: "auto" }} >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>Actions</TableCell>
              <TableCell sx={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>Sr.No</TableCell>
              {fixedColumns.map((col) => {
              if (isColumnHidden(col)) return null;
              return (
                <TableCell key={col.key} style={{ backgroundColor: "#f5f5f5", fontWeight: 600, textWrap: "nowrap" }}>{col.label}</TableCell>
              );})}
              {/* Extra column headers from props */}
              {extraColumns.map((extraKey, index) => (
                <TableCell key={`extra-header-${index}`}>
                  {extraKey.charAt(0).toUpperCase() + extraKey.slice(1)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {testRows.map((row, rowIndex) => (
              <TableRow 
                key={rowIndex}
                style={{ backgroundColor: "white" }}
                hover
              >
                {/* Actions Column */}
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <ButtonGroup size="small" style={{ gap: "5px", alignItems: "center" }}>
                    {(permissions.copy || permissions.allAccess) && (
                    <>
                    <TextField
                      style={{ width: "50px", marginRight: "10px" }}
                      variant="standard"
                      type="number"
                      InputProps={{ inputProps: { min: 1, max: 75 } }}
                      size="small"
                      value={copyQt[rowIndex] || 1}
                      onChange={(e) => {
                        const newCopyQt = [...copyQt];
                        newCopyQt[rowIndex] = Number(e.target.value) || 1;
                        setCopyQt(newCopyQt);
                      }}
                    />
                    {!(testRows.length > 0 && inputStates.location == 2) && (                    
                    <Tooltip title={`Copy ${copyQt[rowIndex] || 1}`}>
                      <ContentCopyIcon
                          style={{ color: '#1976d2', cursor: 'pointer' }}
                          onClick={() => copyRow(rowIndex)}
                        />
                    </Tooltip>
                    )}
                    </>
                    )}

                    <Tooltip title="Open In Modal">
                      <PreviewIcon
                        style={{ color: "#1976d2", cursor: "pointer" }}
                        onClick={() => openModalEdit(true, rowIndex, row)}
                      />
                    </Tooltip>
                    {!(testRows.length > 0 && inputStates.location == 2) && (
                    (permissions.delete || permissions.allAccess) && (
                      <Tooltip title="Delete Row">
                        <DeleteIcon
                          style={{ cursor: "pointer", color: '#dc3545' }}
                          onClick={() => deleteRow(rowIndex)}
                        />
                      </Tooltip>
                    )
                    )}
                  </ButtonGroup>
                </TableCell>

                {/* Sr. No */}
                <TableCell onClick={() => handleRowClick(rowIndex)}>
                  {rowIndex + 1}
                </TableCell>

                {/* Fixed Columns */}
                {fixedColumns.map((column) => {
                  if (isColumnHidden(column)) return null;
                  return renderCell(row, rowIndex, column, editingIndex === rowIndex);
                })}

                {/* Extra Columns */}
                {renderExtraColumns(row, rowIndex, editingIndex === rowIndex)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Modal */}
      <Dialog
        open={openModal.open}
        onClose={() => {
          setOpenModal({ open: false, index: null });
          setEditingIndex(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Test Row</DialogTitle>
        <DialogContent dividers>
          {openModal.index !== null && testRows[openModal.index] && (
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-3 gap-5">
              {fixedColumns.map((column) => {
                if (isColumnHidden(column)) return null;
                return (
                <div key={column.key}>
                  {renderCell(testRows[openModal.index], openModal.index, column, true, true)}
                </div>
              )})}
              {/* Render extra columns in modal */}
              {extraColumns.map((extraKey, index) => (
                <div key={`modal-extra-${index}`}>
                  <TableCell style={{ border: "none", padding: "0px" }}>
                    <TextField
                      style={{ width: "220px" }}
                      size="small"
                      value={testRows[openModal.index].extraColumns?.[extraKey] || ""}
                      onChange={(e) => {
                        updateCellValue(openModal.index, "extraColumns", {
                          ...testRows[openModal.index].extraColumns,
                          [extraKey]: e.target.value
                        });
                      }}
                      label={extraKey.charAt(0).toUpperCase() + extraKey.slice(1)}
                      inputProps={{ autoComplete: 'off' }}
                    />
                  </TableCell>
                </div>
              ))}
            </div>
          )}
          
          <div className="flex justify-end mt-4 gap-2">
            <Button
              variant="outlined"
              onClick={() => {
                setOpenModal({ open: false, index: null });
                setEditingIndex(null);
              }}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InwardTestRows;